import React, { useEffect, useState } from 'react';

// Admin — event galleries.
//
// One gallery per event we covered; one row per person photographed. The
// panel does the whole job in order: make the gallery, add the portraits
// (upload, Drive link, or paste a list), publish, send everyone "your photo is
// ready", then watch downloads and subscribers come in.

const API_BASE = import.meta.env.VITE_API_URL || '';

const authHeader = () => {
  const t = localStorage.getItem('hddn_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
// <img> cannot send a header, so thumbnails of draft galleries carry the token.
const thumbUrl = (gallerySlug, photoSlug, w) =>
  `${API_BASE}/api/event-galleries/${encodeURIComponent(gallerySlug)}/photos/${encodeURIComponent(photoSlug)}/hero?w=${w}&t=${encodeURIComponent(localStorage.getItem('hddn_token') || '')}`;

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeader(), ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}), ...(options.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `${res.status} ${res.statusText}`);
  return d;
}

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A pasted list: JSON array, or one person per line as
//   Name, email, drive link or id[, caption]
// Tabs work too (straight out of a spreadsheet).
function parsePasted(text) {
  const t = (text || '').trim();
  if (!t) return [];
  if (t.startsWith('[')) {
    const arr = JSON.parse(t);
    if (!Array.isArray(arr)) throw new Error('JSON must be an array.');
    return arr;
  }
  return t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const cells = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map((c) => c.trim().replace(/^"|"$/g, ''));
    // Columns can arrive in any order: whichever cell is an email is the email,
    // whichever looks like Drive is the file, the first leftover is the name.
    let email = null, drive = null;
    const rest = [];
    for (const cell of cells) {
      if (!email && EMAIL_OK.test(cell)) email = cell;
      else if (!drive && (/drive\.google\.com|docs\.google\.com/.test(cell) || /^[a-zA-Z0-9_-]{20,}$/.test(cell))) drive = cell;
      else rest.push(cell);
    }
    return { name: rest[0] || '', email, drive, caption: rest[1] || undefined };
  });
}

// "SOTC-12_Jane-Doe.jpg" → "Jane Doe". A bare camera number stays as is so
// the row is visibly still waiting for a name.
// "CasaHalloween26-17.jpg" and "DSC_0412.jpg" → "" (a party frame, no
// person to name), so the card shows the photo alone.
function nameFromFilename(filename) {
  const stem = String(filename || '').replace(/\.[^.]+$/, '');
  if (/^[A-Za-z]+\d*[-_ ]?\d+$/.test(stem)) return '';
  const cleaned = stem.replace(/^[A-Za-z]{2,6}[-_ ]?\d+[-_ ]*/, '').replace(/[-_]+/g, ' ').trim();
  return /[A-Za-z]{2}/.test(cleaned) ? cleaned : '';
}

const timeAgo = (ts) => {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : new Date(ts).toLocaleDateString();
};

export default function EventGalleriesPanel({ palette, fonts }) {
  const [galleries, setGalleries] = useState(null);
  const [selected, setSelected] = useState(null); // gallery id
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ title: '', kind: 'event', subtitle: '', eventDate: '', blurb: '', credit: '', driveFolderUrl: '', signupOpen: false, bookingOpen: false, priceCents: 0, location: '', bookingBlurb: '' });
  const [slots, setSlots] = useState([]);
  const [slotGen, setSlotGen] = useState({ date: '', start: '10:00', end: '14:00', minutes: 20, capacity: 1 });
  const [editing, setEditing] = useState(null); // gallery edit draft
  const [pasted, setPasted] = useState('');
  const [folderUrl, setFolderUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [one, setOne] = useState({ name: '', email: '', drive: '', caption: '' });
  const [files, setFiles] = useState([]);           // browser upload queue
  const [uploading, setUploading] = useState(false);
  // One bar for whichever batch is running: { label, done, total, failures: [{ name, error }], finished }
  const [progress, setProgress] = useState(null);
  const [dupes, setDupes] = useState(null); // { groups, extra } after a check
  const [participants, setParticipants] = useState([]);
  const [pastedPeople, setPastedPeople] = useState('');
  const [rowDrafts, setRowDrafts] = useState({}); // photoId -> { name, email, caption }
  const [view, setView] = useState('grid');        // grid (contact sheet) | table
  const [picked, setPicked] = useState(new Set()); // photo ids ticked in the grid

  const loadGalleries = async () => {
    try {
      const d = await api('/api/admin/event-galleries');
      setGalleries(d.galleries || []);
      if (!selected && d.galleries?.length) setSelected(d.galleries[0].id);
    } catch (err) { setNote(`✗ ${err.message}`); setGalleries([]); }
  };
  const loadPhotos = async (id) => {
    if (!id) { setPhotos([]); setParticipants([]); return; }
    try {
      const d = await api(`/api/admin/event-galleries/${id}/photos`);
      setPhotos(d.photos || []);
      const pp = await api(`/api/admin/event-galleries/${id}/participants`);
      setParticipants(pp.participants || []);
      const sl = await api(`/api/admin/event-galleries/${id}/slots`);
      setSlots(sl.slots || []);
    } catch (err) { setNote(`✗ ${err.message}`); }
  };
  useEffect(() => { loadGalleries(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPhotos(selected); setProgress(null); setDupes(null); setRowDrafts({}); setPicked(new Set()); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const gallery = (galleries || []).find((g) => g.id === selected) || null;
  const refresh = () => { loadGalleries(); loadPhotos(selected); };

  const run = async (fn, okNote) => {
    setBusy(true); setNote('');
    try { const out = await fn(); if (okNote) setNote(typeof okNote === 'function' ? okNote(out) : okNote); refresh(); return out; }
    catch (err) { setNote(`✗ ${err.message}`); }
    finally { setBusy(false); }
  };

  // ---- galleries -----------------------------------------------------------
  const createGallery = () => run(async () => {
    const g = await api('/api/admin/event-galleries', { method: 'POST', body: JSON.stringify(draft) });
    setShowNew(false); setDraft({ title: '', kind: 'event', subtitle: '', eventDate: '', blurb: '', credit: '', driveFolderUrl: '', signupOpen: false, bookingOpen: false, priceCents: 0, location: '', bookingBlurb: '' });
    setSelected(g.id);
    return g;
  }, (g) => `✓ Created — it will live at ${g.pageUrl} once published.`);

  const saveGallery = () => run(async () => {
    await api(`/api/admin/event-galleries/${editing.id}`, { method: 'PUT', body: JSON.stringify(editing) });
    setEditing(null);
  }, '✓ Saved.');

  const togglePublished = (g) => {
    if (!g.published && !photos.some((p) => p.has_hero)) { setNote('Add at least one photo before publishing.'); return; }
    if (g.published && !confirm('Unpublish this gallery? Every link in the emails you sent will stop working until it is published again.')) return;
    run(() => api(`/api/admin/event-galleries/${g.id}`, { method: 'PUT', body: JSON.stringify({ published: !g.published }) }),
      g.published ? 'Unpublished.' : `✓ Live at ${g.pageUrl}`);
  };

  const deleteGallery = (g) => {
    if (!confirm(`Delete “${g.title}” and all ${g.photos} photos? This cannot be undone.`)) return;
    run(async () => { await api(`/api/admin/event-galleries/${g.id}`, { method: 'DELETE' }); setSelected(null); }, 'Deleted.');
  };

  // ---- photos --------------------------------------------------------------
  const addOne = () => run(async () => {
    if (!one.drive) throw new Error('Paste a Drive link.');
    await api(`/api/admin/event-galleries/${gallery.id}/photos/from-drive`, {
      method: 'POST', body: JSON.stringify({ ...one, url: one.drive }),
    });
    setOne({ name: '', email: '', drive: '', caption: '' });
  }, '✓ Added.');

  // One request per file, in sequence: a 60-file export is a few hundred MB,
  // and one failure should cost one file, not the batch.
  const uploadAll = async () => {
    if (!files.length) return;
    setUploading(true); setNote('');
    const failures = [];
    let ok = 0;
    setProgress({ label: 'Uploading', done: 0, total: files.length, failures, finished: false });
    for (const file of files) {
      const form = new FormData();
      form.append('photo', file);
      form.append('name', nameFromFilename(file.name));
      try {
        await api(`/api/admin/event-galleries/${gallery.id}/photos`, { method: 'POST', body: form });
        ok++;
      } catch (err) {
        failures.push({ name: file.name, error: err.message });
      }
      setProgress({ label: 'Uploading', done: ok + failures.length, total: files.length, failures: [...failures], finished: false });
    }
    const failed = failures.length;
    setProgress({ label: 'Uploaded', done: ok + failed, total: files.length, failures, finished: true });
    setNote(`✓ ${ok} uploaded${failed ? `, ${failed} failed` : ''}.${gallery.kind === 'portraits' ? ' Fix names and add emails in the table, or paste the sign-in sheet below to attach emails by name.' : ''}`);
    setUploading(false);
    if (!failed) setFiles([]);
    refresh();
  };

  const savePhoto = (p) => {
    const d = rowDrafts[p.id];
    if (!d) return;
    run(async () => {
      await api(`/api/admin/event-galleries/${gallery.id}/photos/${p.id}`, { method: 'PUT', body: JSON.stringify(d) });
      setRowDrafts((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
    }, '✓ Saved.');
  };
  const togglePhoto = (p) => run(() => api(`/api/admin/event-galleries/${gallery.id}/photos/${p.id}`, {
    method: 'PUT', body: JSON.stringify({ published: !p.published }),
  }));
  // Duplicates: same Drive file or identical bytes under two rows.
  const checkDupes = () => run(async () => {
    const d = await api(`/api/admin/event-galleries/${gallery.id}/duplicates`);
    setDupes(d);
    return d;
  }, (d) => (d.extra ? `${d.extra} duplicate ${d.extra === 1 ? 'frame' : 'frames'} in ${d.groups.length} ${d.groups.length === 1 ? 'group' : 'groups'}.` : '✓ No duplicates.'));
  const removeDupes = () => {
    if (!dupes?.extra || !confirm(`Remove ${dupes.extra} duplicate ${dupes.extra === 1 ? 'frame' : 'frames'}? The copy with the most information is kept.`)) return;
    run(async () => {
      const d = await api(`/api/admin/event-galleries/${gallery.id}/duplicates/remove`, { method: 'POST', body: '{}' });
      setDupes(null);
      return d;
    }, (d) => `✓ Removed ${d.removed} duplicate ${d.removed === 1 ? 'frame' : 'frames'}.`);
  };

  // Contact sheet: tick frames, then hide/show/delete them in one go.
  const togglePick = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const bulk = (action) => {
    const ids = [...picked];
    if (!ids.length) return;
    if (action === 'delete' && !confirm(`Delete ${ids.length} photo${ids.length === 1 ? '' : 's'} from the gallery? This cannot be undone.`)) return;
    run(async () => {
      await api(`/api/admin/event-galleries/${gallery.id}/photos/bulk`, { method: 'POST', body: JSON.stringify({ ids, action }) });
      setPicked(new Set());
    }, `✓ ${ids.length} ${action === 'hide' ? 'hidden' : action === 'show' ? 'shown' : 'deleted'}.`);
  };

  const deletePhoto = (p) => {
    if (!confirm(`Remove ${p.subject_name || 'this portrait'} from the gallery?`)) return;
    run(() => api(`/api/admin/event-galleries/${gallery.id}/photos/${p.id}`, { method: 'DELETE' }), 'Removed.');
  };

  // Turn a public folder into paste lines, so the operator can still see and
  // edit what is about to be imported.
  const listFolder = () => run(async () => {
    const d = await api(`/api/admin/drive-folder?url=${encodeURIComponent(folderUrl.trim())}`);
    const lines = d.files.map((f) => `${nameFromFilename(f.name)}, , https://drive.google.com/file/d/${f.id}/view`);
    setPasted(lines.join('\n'));
    return d;
  }, (d) => `✓ ${d.files.length} photos found${d.total > d.files.length ? ` (${d.total - d.files.length} non-image files skipped)` : ''}. Check the list, then Import.`);

  // ---- import --------------------------------------------------------------
  const runImport = async ({ dryRun = false } = {}) => {
    let entries;
    try { entries = parsePasted(pasted); } catch (err) { setNote(`✗ ${err.message}`); return; }
    if (!entries.length) { setNote('Paste a list first.'); return; }
    if (!dryRun && !confirm(`Import ${entries.length} portrait${entries.length === 1 ? '' : 's'} from Drive into “${gallery.title}”?`)) return;
    setImporting(true); setNote('');
    const label = dryRun ? 'Checking' : 'Importing';
    const failures = [];
    let offset = 0, done = 0;
    setProgress({ label, done: 0, total: entries.length, failures, finished: false });
    try {
      for (;;) {
        const d = await api(`/api/admin/event-galleries/${gallery.id}/import`, {
          method: 'POST', body: JSON.stringify({ entries, offset, dryRun, publish: true }),
        });
        for (const r of d.results) {
          if (r.ok) done++; else failures.push({ name: r.name, error: r.error });
        }
        setProgress({ label, done: done + failures.length, total: d.total, failures: [...failures], finished: false });
        if (d.nextOffset === null) break;
        offset = d.nextOffset;
      }
      const failed = failures.length;
      setProgress({ label: dryRun ? 'Checked' : 'Imported', done: done + failed, total: entries.length, failures, finished: true });
      setNote(dryRun
        ? `Dry run: ${done} look fine${failed ? `, ${failed} with problems` : ''}. Nothing changed.`
        : `✓ ${done} imported${failed ? `, ${failed} failed` : ''}.`);
      if (!dryRun) { refresh(); if (!failed) setPasted(''); }
    } catch (err) {
      setProgress((prev) => (prev ? { ...prev, finished: true } : prev));
      setNote(`✗ ${err.message}`);
    }
    finally { setImporting(false); }
  };

  // ---- slots & bookings ----------------------------------------------------
  const generateSlots = () => {
    if (!slotGen.date) { setNote('Pick a date.'); return; }
    // Built in this browser's zone — the operator is in New Rochelle, so 10:00 means 10:00 here.
    const startsAt = new Date(`${slotGen.date}T${slotGen.start}`), endsAt = new Date(`${slotGen.date}T${slotGen.end}`);
    run(() => api(`/api/admin/event-galleries/${gallery.id}/slots`, {
      method: 'POST', body: JSON.stringify({ startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString(), minutes: Number(slotGen.minutes), capacity: Number(slotGen.capacity) }),
    }), (d) => `✓ ${d.created} slot${d.created === 1 ? '' : 's'} added.`);
  };
  const deleteSlot = (sl) => run(() => api(`/api/admin/event-galleries/${gallery.id}/slots/${sl.id}`, { method: 'DELETE' }), 'Removed.');
  const setBooking = (b, status) => {
    if (status === 'cancelled' && !confirm(`Cancel ${b.name}'s booking? This frees the slot. No refund is issued here.`)) return;
    run(() => api(`/api/admin/event-galleries/${gallery.id}/bookings/${b.id}`, { method: 'PUT', body: JSON.stringify({ status }) }),
      status === 'confirmed' ? `✓ ${b.name} confirmed — confirmation email sent.` : 'Cancelled.');
  };

  // ---- participants --------------------------------------------------------
  const addParticipants = () => {
    let entries;
    try { entries = parsePasted(pastedPeople); } catch (err) { setNote(`✗ ${err.message}`); return; }
    const people = entries.filter((e) => e.email);
    if (!people.length) { setNote('Paste at least one line with an email.'); return; }
    run(async () => {
      const d = await api(`/api/admin/event-galleries/${gallery.id}/participants`, { method: 'POST', body: JSON.stringify({ entries: people }) });
      setPastedPeople('');
      return d;
    }, (d) => `✓ ${d.added} ${d.added === 1 ? 'person' : 'people'} on the list${d.skipped ? `, ${d.skipped} skipped (no valid email)` : ''}.`);
  };
  const removeParticipant = (p) => run(() => api(`/api/admin/event-galleries/${gallery.id}/participants/${p.id}`, { method: 'DELETE' }));

  // ---- announce ------------------------------------------------------------
  const announce = (body, confirmText) => {
    if (confirmText && !confirm(confirmText)) return;
    run(() => api(`/api/admin/event-galleries/${gallery.id}/announce`, { method: 'POST', body: JSON.stringify(body) }),
      (d) => d.test
        ? (d.ok ? `✓ Test sent to ${d.to}.` : `✗ Test failed: ${d.error}`)
        : `✓ Sent ${d.sent}${d.failed ? `, ${d.failed} failed` : ''}${d.skipped ? `, ${d.skipped} skipped (no email)` : ''}.${d.errors?.length ? ` ${d.errors.join(' · ')}` : ''}`);
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/event-galleries/${gallery.id}/emails.csv`, { headers: authHeader() });
      if (!r.ok) throw new Error('Could not build the list');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${gallery.slug}-emails.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  // ---- styles --------------------------------------------------------------
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: palette.textMuted, whiteSpace: 'nowrap' };
  const td = { padding: '10px', fontFamily: fonts, fontSize: 12, color: palette.text, verticalAlign: 'top', borderBottom: `1px solid ${palette.border}` };
  const btn = (bg, fg, extra = {}) => ({
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    padding: '6px 11px', background: bg, color: fg, border: 'none', cursor: busy ? 'wait' : 'pointer', ...extra,
  });
  const ghost = (extra = {}) => btn('none', palette.brown, { border: `1px solid ${palette.border}`, ...extra });
  const input = { fontFamily: fonts, fontSize: 12, padding: '7px 9px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: '100%', boxSizing: 'border-box' };
  const box = { border: `1px solid ${palette.border}`, background: palette.white, padding: 14, marginBottom: 12 };
  const toolBox = { border: `1px solid ${palette.border}`, background: palette.white, padding: '0 12px 12px', marginBottom: 10 };
  const toolSummary = { fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, cursor: 'pointer', padding: '12px 0', outline: 'none', listStyle: 'none' };
  const label = { fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4, display: 'block' };
  const stat = (n, l) => (
    <div key={l} style={{ border: `1px solid ${palette.border}`, background: palette.white, padding: '10px 14px', minWidth: 110 }}>
      <div style={{ fontFamily: fonts, fontSize: 22, fontWeight: 700, color: palette.brown, lineHeight: 1 }}>{n}</div>
      <div style={{ ...label, marginTop: 6, marginBottom: 0 }}>{l}</div>
    </div>
  );

  // Progress for a batch: a bar, a count, and only the failures spelled out.
  const progressBar = progress && (() => {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    const okCount = progress.done - progress.failures.length;
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: palette.textMuted, marginBottom: 4 }}>
          <span>{progress.label} {progress.done} of {progress.total}{progress.finished ? '' : '…'}</span>
          <span>{progress.finished ? `${okCount} ok${progress.failures.length ? ` · ${progress.failures.length} failed` : ''}` : `${pct}%`}</span>
        </div>
        <div style={{ height: 6, background: palette.cream, border: `1px solid ${palette.border}`, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: progress.failures.length ? palette.red : palette.sage, transition: 'width .2s' }} />
        </div>
        {!!progress.failures.length && (
          <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 12, lineHeight: 1.7, color: palette.red }}>
            {progress.failures.map((f, i) => <li key={i}>✗ {f.name} — {f.error}</li>)}
          </ul>
        )}
      </div>
    );
  })();

  if (galleries === null) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading galleries…</div>;

  const frameEmails = new Set(photos.filter((p) => p.subject_email).map((p) => p.subject_email.toLowerCase()));
  const listEmails = new Set(participants.map((p) => p.email.toLowerCase()));
  // Frames with an address join the list at send time, so count them now.
  const notOnList = [...frameEmails].filter((e) => !listEmails.has(e)).length;
  const totalPeople = participants.length + notOnList;
  const unannounced = participants.filter((p) => !p.sent_at).length + notOnList;
  const withEmail = totalPeople;

  // Name and date are the gallery. Everything else has a sensible default
  // (credit reads "Photographed by HDDN LAB" when blank) and lives behind a
  // disclosure for the times it matters.
  const galleryForm = (g, set, onSave, onCancel, saveLabel) => (
    <div style={box}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <div><span style={label}>Event name</span><input style={input} value={g.title} onChange={(e) => set({ ...g, title: e.target.value })} placeholder="Casaroma Halloween Party" autoFocus /></div>
        <div><span style={label}>Date (as shown)</span><input style={input} value={g.eventDate || ''} onChange={(e) => set({ ...g, eventDate: e.target.value })} placeholder="Friday, October 31" /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <span style={{ ...label, marginBottom: 0 }}>Type</span>
        {[['event', 'Event coverage'], ['portraits', 'Portraits (one per person)']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => set({ ...g, kind: k })}
            style={btn((g.kind || 'event') === k ? palette.brown : palette.white, (g.kind || 'event') === k ? palette.white : palette.brown, { border: `1px solid ${palette.border}` })}>{l}</button>
        ))}
        <span style={{ fontSize: 11, color: palette.textMuted }}>
          {(g.kind || 'event') === 'portraits' ? 'Names, search by name, “find yourself”, one email per sitter.' : 'No names needed; “all photos” wording; optional whole-set download.'}
        </span>
      </div>
      {/* Doors in: a sign-up sheet, and bookings for an open call */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, padding: '10px 12px', background: palette.cream, border: `1px solid ${palette.border}` }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: palette.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!g.signupOpen} onChange={(e) => set({ ...g, signupOpen: e.target.checked })} />
          <span><b>Sign-up sheet</b> — a page where people leave name and email (the clipboard at the door)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: palette.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!g.bookingOpen} onChange={(e) => set({ ...g, bookingOpen: e.target.checked })} />
          <span><b>Bookings</b> — people pick a time slot and pay (open calls, holiday shoots)</span>
        </label>
        {g.bookingOpen && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, width: '100%' }}>
            <div><span style={label}>Price per slot ($)</span><input style={input} type="number" min="0" step="1" value={Math.round((g.priceCents || 0) / 100)} onChange={(e) => set({ ...g, priceCents: Math.round(Number(e.target.value || 0) * 100) })} /></div>
            <div style={{ gridColumn: 'span 2' }}><span style={label}>Location</span><input style={input} value={g.location || ''} onChange={(e) => set({ ...g, location: e.target.value })} placeholder="HDDN LAB, 72 Centre Avenue, New Rochelle" /></div>
            <div style={{ gridColumn: '1 / -1' }}><span style={label}>What they’re booking (shown above the times)</span><input style={input} value={g.bookingBlurb || ''} onChange={(e) => set({ ...g, bookingBlurb: e.target.value })} placeholder="A 20-minute holiday portrait session. Up to four people; bring the dog." /></div>
          </div>
        )}
      </div>
      <details style={{ marginTop: 10 }} open={!!(g.subtitle || g.blurb || g.credit || g.driveFolderUrl)}>
        <summary style={{ ...toolSummary, padding: '6px 0' }}>Optional details</summary>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 6 }}>
          <div><span style={label}>Full-set Drive folder</span><input style={input} value={g.driveFolderUrl || ''} onChange={(e) => set({ ...g, driveFolderUrl: e.target.value })} placeholder="https://drive.google.com/drive/folders/… (adds a “whole set” download)" /></div>
          <div><span style={label}>Subtitle</span><input style={input} value={g.subtitle || ''} onChange={(e) => set({ ...g, subtitle: e.target.value })} placeholder="Portraits from the Mayor’s State of the City" /></div>
          <div><span style={label}>Credit line</span><input style={input} value={g.credit || ''} onChange={(e) => set({ ...g, credit: e.target.value })} placeholder="Photographed by HDDN LAB (default)" /></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <span style={label}>Intro (sidebar on the gallery, and in the email)</span>
          <textarea style={{ ...input, minHeight: 60 }} value={g.blurb || ''} onChange={(e) => set({ ...g, blurb: e.target.value })}
            placeholder="Everyone who stopped by our portrait setup is here. Find yourself, download the full-size file — it’s on us." />
        </div>
      </details>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={onSave} disabled={busy || !g.title.trim()} style={btn(palette.brown, palette.white)}>{saveLabel}</button>
        <button onClick={onCancel} style={ghost()}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: fonts }}>
      {/* Gallery picker */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {galleries.map((g) => (
          <button key={g.id} onClick={() => setSelected(g.id)}
            style={btn(g.id === selected ? palette.brown : palette.white, g.id === selected ? palette.white : palette.brown, { border: `1px solid ${palette.border}` })}>
            {g.title}{g.published ? '' : ' · draft'}
          </button>
        ))}
        <button onClick={() => setShowNew((v) => !v)} style={ghost({ borderStyle: 'dashed' })}>+ New gallery</button>
      </div>

      {showNew && galleryForm(draft, setDraft, createGallery, () => setShowNew(false), 'Create')}

      {note && <div style={{ fontFamily: fonts, fontSize: 12, color: note.startsWith('✗') ? palette.red : palette.sage, marginBottom: 10 }}>{note}</div>}

      {!gallery && !showNew && (
        <div style={{ ...box, color: palette.textMuted, fontSize: 13, lineHeight: 1.7 }}>
          No galleries yet. Make one for the event, add the portraits, publish, then send everyone their link.
        </div>
      )}

      {gallery && (
        <>
          {/* Header + stats */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 12 }}>
            {stat(gallery.photos, gallery.kind === 'portraits' ? 'portraits' : 'photos')}
            {stat(totalPeople, 'people')}
            {gallery.booking_open || gallery.bookings ? stat(gallery.bookings || 0, 'booked') : null}
            {stat(gallery.announced, 'emailed')}
            {stat(gallery.downloaders, 'downloaded')}
            {stat(gallery.subscribers, 'subscribed')}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {gallery.published
                ? <a href={gallery.pageUrl} target="_blank" rel="noreferrer" style={{ ...ghost(), textDecoration: 'none', display: 'inline-block' }}>Open ↗</a>
                : null}
              <button onClick={() => togglePublished(gallery)} disabled={busy} style={btn(gallery.published ? palette.white : palette.sage, gallery.published ? palette.brown : palette.white, gallery.published ? { border: `1px solid ${palette.border}` } : {})}>
                {gallery.published ? 'Unpublish' : 'Publish'}
              </button>
              <button onClick={() => setEditing(editing ? null : { id: gallery.id, title: gallery.title, kind: gallery.kind || 'event', subtitle: gallery.subtitle, eventDate: gallery.event_date, blurb: gallery.blurb, credit: gallery.credit, driveFolderUrl: gallery.drive_folder_url, signupOpen: !!gallery.signup_open, bookingOpen: !!gallery.booking_open, priceCents: gallery.price_cents || 0, location: gallery.location || '', bookingBlurb: gallery.booking_blurb || '' })} style={ghost()}>Edit</button>
              <button onClick={() => deleteGallery(gallery)} style={ghost({ color: palette.red })}>Delete</button>
            </div>
          </div>
          {editing && galleryForm(editing, setEditing, saveGallery, () => setEditing(null), 'Save')}

          {(gallery.signup_open || gallery.booking_open) && gallery.published && (
            <div style={{ ...box, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
              <span style={{ ...label, marginBottom: 0 }}>Share</span>
              {gallery.signup_open && <a href={`${gallery.pageUrl}/signup`} target="_blank" rel="noreferrer" style={{ color: palette.orange }}>hddnlab.com{gallery.pageUrl}/signup ↗</a>}
              {gallery.booking_open && <a href={`${gallery.pageUrl}/book`} target="_blank" rel="noreferrer" style={{ color: palette.orange }}>hddnlab.com{gallery.pageUrl}/book ↗</a>}
            </div>
          )}
          {(gallery.signup_open || gallery.booking_open) && !gallery.published && (
            <div style={{ ...box, fontSize: 12, color: palette.red }}>Publish the gallery to open the sign-up / booking pages — they 404 on a draft.</div>
          )}

          {/* Slots & bookings */}
          {gallery.booking_open && (
            <details style={toolBox} open={!slots.length}>
              <summary style={toolSummary}>Time slots & bookings{gallery.bookings ? ` · ${gallery.bookings} booked` : ''}</summary>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 10 }}>
                <div><span style={label}>Date</span><input style={input} type="date" value={slotGen.date} onChange={(e) => setSlotGen({ ...slotGen, date: e.target.value })} /></div>
                <div><span style={label}>From</span><input style={input} type="time" value={slotGen.start} onChange={(e) => setSlotGen({ ...slotGen, start: e.target.value })} /></div>
                <div><span style={label}>To</span><input style={input} type="time" value={slotGen.end} onChange={(e) => setSlotGen({ ...slotGen, end: e.target.value })} /></div>
                <div><span style={label}>Minutes each</span><input style={input} type="number" min="5" step="5" value={slotGen.minutes} onChange={(e) => setSlotGen({ ...slotGen, minutes: e.target.value })} /></div>
                <div><span style={label}>Spots per slot</span><input style={input} type="number" min="1" value={slotGen.capacity} onChange={(e) => setSlotGen({ ...slotGen, capacity: e.target.value })} /></div>
                <button onClick={generateSlots} disabled={busy || !slotGen.date} style={btn(palette.brown, palette.white)}>Add slots</button>
              </div>
              {!slots.length && <div style={{ fontSize: 12, color: palette.textMuted }}>No slots yet. Add a day above; re-adding the same times updates them.</div>}
              {!!slots.length && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {slots.map((sl) => (
                      <tr key={sl.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{sl.label}</td>
                        <td style={{ ...td, color: palette.textMuted, whiteSpace: 'nowrap' }}>{sl.capacity - sl.remaining}/{sl.capacity}</td>
                        <td style={td}>
                          {sl.bookings.map((b) => (
                            <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                              <span style={{ fontWeight: b.status === 'confirmed' ? 700 : 400, color: b.status === 'cancelled' ? palette.textMuted : palette.text, textDecoration: b.status === 'cancelled' ? 'line-through' : 'none' }}>{b.name}</span>
                              <span style={{ color: palette.textMuted, fontSize: 11 }}>{b.email}{b.phone ? ` · ${b.phone}` : ''}{b.amount_cents ? ` · $${(b.amount_cents / 100).toFixed(0)}` : ''}</span>
                              <span style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: b.status === 'confirmed' ? palette.sage : b.status === 'pending' ? palette.orange : palette.textMuted }}>{b.status}</span>
                              {b.status !== 'confirmed' && b.status !== 'cancelled' && <button onClick={() => setBooking(b, 'confirmed')} disabled={busy} style={ghost()}>Mark paid</button>}
                              {b.status !== 'cancelled' && <button onClick={() => setBooking(b, 'cancelled')} disabled={busy} style={ghost({ color: palette.red })}>Cancel</button>}
                            </div>
                          ))}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {!sl.bookings.some((b) => b.status === 'confirmed') && <button onClick={() => deleteSlot(sl)} disabled={busy} style={ghost({ color: palette.red })}>Remove</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </details>
          )}

          {/* Add portraits */}
          <details style={toolBox} open={!photos.length}>
            <summary style={toolSummary}>{gallery.kind === 'portraits' ? 'Add portraits' : 'Add photos'}</summary>
            <div style={{ fontSize: 12, color: palette.textMuted, lineHeight: 1.7, marginBottom: 10 }}>
              <b>1. Upload the photos.</b> Select them all at once. {gallery.kind === 'portraits'
                ? 'Each becomes a portrait named from its filename (“SOTC-12 Jane Doe.jpg” → Jane Doe); fix names in the table afterwards.'
                : 'Camera-numbered files come in unnamed, which is what you want for coverage.'} Originals are kept for the download.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
              <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} style={{ fontSize: 12 }} disabled={uploading} />
              <button onClick={uploadAll} disabled={uploading || busy || !files.length} style={btn(palette.brown, palette.white)}>
                {uploading ? 'Uploading…' : `Upload ${files.length || ''} ${files.length === 1 ? 'photo' : 'photos'}`}
              </button>
            </div>
            {progress && /Upload/.test(progress.label) && <div style={{ marginBottom: 14 }}>{progressBar}</div>}
            <div style={{ fontSize: 12, color: palette.textMuted, lineHeight: 1.7, marginBottom: 10, borderTop: `1px solid ${palette.border}`, paddingTop: 12 }}>
              {gallery.kind === 'portraits'
                ? <><b>2. Paste the sign-in sheet</b> — one person per line, <b>name, email</b> (tabs from a spreadsheet work). Emails attach to the uploaded photo with the matching name. Add a <b>Drive link</b> as a third column to import the photo from Drive instead (file must be shared “anyone with the link”).</>
                : <><b>2. Or import from Drive.</b> Paste a folder link below and “List folder”, or one Drive link per line (files shared “anyone with the link”).</>}
              {' '}Re-running updates rather than duplicates. The gallery stays a draft until you hit Publish.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <input style={{ ...input, flex: '1 1 260px', width: 'auto' }} placeholder="Or paste a Drive folder link (shared “anyone with the link”) to list every photo in it" value={folderUrl} onChange={(e) => setFolderUrl(e.target.value)} />
              <button onClick={listFolder} disabled={busy || !folderUrl.trim()} style={ghost()}>List folder</button>
            </div>
            <textarea style={{ ...input, minHeight: 110, fontFamily: 'monospace' }} value={pasted} onChange={(e) => setPasted(e.target.value)}
              placeholder={'Jane Doe, jane@example.com\nSam Lee, sam@example.com, https://drive.google.com/file/d/1AbC…/view'} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button onClick={() => runImport({ dryRun: true })} disabled={importing || busy} style={ghost()}>Dry run</button>
              <button onClick={() => runImport()} disabled={importing || busy} style={btn(palette.brown, palette.white)}>{importing ? 'Importing…' : 'Import'}</button>
            </div>
            {progress && !/Upload/.test(progress.label) && <div style={{ marginTop: 10 }}>{progressBar}</div>}

            <div style={{ borderTop: `1px solid ${palette.border}`, marginTop: 14, paddingTop: 12 }}>
              <div style={{ ...label, marginBottom: 8 }}>Or add one from Drive</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                <input style={input} placeholder="Name" value={one.name} onChange={(e) => setOne({ ...one, name: e.target.value })} />
                <input style={input} placeholder="Email" value={one.email} onChange={(e) => setOne({ ...one, email: e.target.value })} />
                <input style={input} placeholder="Drive link or id" value={one.drive} onChange={(e) => setOne({ ...one, drive: e.target.value })} />
                <input style={input} placeholder="Caption (optional)" value={one.caption} onChange={(e) => setOne({ ...one, caption: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={addOne} disabled={busy || !one.drive} style={btn(palette.brown, palette.white)}>Add</button>
              </div>
            </div>
          </details>

          {/* Participants & the one email */}
          <details style={toolBox}>
            <summary style={toolSummary}>Email the people who were there{unannounced ? ` · ${unannounced} waiting` : ''}</summary>
            <div style={{ fontSize: 12, color: palette.textMuted, lineHeight: 1.7, marginBottom: 10 }}>
              Paste the sign-in sheet — <b>name, email</b> per line (tabs from a spreadsheet work). Each person gets one email with a link to the whole
              gallery; they pick their own frames and the download is one tap, no form. The last line of the email is a one-click newsletter link — that
              tap is the consent, nothing before it. People already emailed are skipped unless you resend.
            </div>
            <textarea style={{ ...input, minHeight: 80, fontFamily: 'monospace' }} value={pastedPeople} onChange={(e) => setPastedPeople(e.target.value)}
              placeholder={'Jane Doe, jane@example.com\nSam Lee, sam@example.com'} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={addParticipants} disabled={busy || !pastedPeople.trim()} style={ghost()}>Add to list</button>
              <span style={{ fontSize: 12, color: palette.textMuted }}>{totalPeople} on the list · {gallery.announced} emailed</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: `1px solid ${palette.border}` }}>
              <button onClick={() => announce({ test: true })} disabled={busy} style={ghost()}>Send me a test</button>
              <button onClick={() => announce({ all: true }, `Email ${unannounced} ${unannounced === 1 ? 'person' : 'people'} now?`)}
                disabled={busy || !gallery.published || !unannounced} style={btn(palette.brown, palette.white)}>
                Send to {unannounced} waiting
              </button>
              <button onClick={() => announce({ all: true, resend: true }, `Resend to everyone on the list (${totalPeople})? They will each get the email again.`)}
                disabled={busy || !gallery.published || !totalPeople} style={ghost()}>Resend to all</button>
              <button onClick={downloadCsv} disabled={busy} style={ghost()}>Export CSV</button>
            </div>
            {!gallery.published && <div style={{ fontSize: 12, color: palette.red, marginTop: 8 }}>Publish the gallery first — the links would 404.</div>}
            {!!participants.length && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ ...toolSummary, padding: '6px 0' }}>Show the list</summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id}>
                        <td style={td}>{p.name || <span style={{ color: palette.textMuted }}>—</span>}</td>
                        <td style={td}>{p.email}</td>
                        <td style={{ ...td, color: palette.textMuted, fontSize: 11 }}>{{ signup: 'signed up at the event', booking: 'booked a slot', frame: 'in a photo', pasted: 'on the list' }[p.source] || p.source}{p.note ? ` · ${p.note}` : ''}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: palette.textMuted }}>{p.sent_at ? `emailed ${timeAgo(p.sent_at)}` : 'not yet'}{p.subscribed_at ? ' · subscribed' : ''}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {gallery.published && <button onClick={() => announce({ inviteId: p.id }, `Email ${p.name || p.email}${p.sent_at ? ' again' : ''}?`)} disabled={busy} style={ghost()}>{p.sent_at ? 'Resend' : 'Email'}</button>}
                            <button onClick={() => removeParticipant(p)} disabled={busy} style={ghost({ color: palette.red })}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </details>

          {/* View switch + bulk actions */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {/* Segmented view switch */}
            <div style={{ display: 'inline-flex', border: `1px solid ${palette.border}` }}>
              {[['grid', 'Sheet'], ['table', 'Table']].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)} style={btn(view === v ? palette.brown : palette.white, view === v ? palette.white : palette.brown, { border: 'none' })}>{l}</button>
              ))}
            </div>
            <span style={{ fontSize: 12, color: palette.textMuted }}>
              {photos.length} {photos.length === 1 ? 'frame' : 'frames'}
              {photos.filter((p) => !p.published).length ? ` · ${photos.filter((p) => !p.published).length} hidden` : ''}
            </span>
            <div style={{ flex: 1 }} />
            {dupes?.extra
              ? <button onClick={removeDupes} disabled={busy} style={btn(palette.red, palette.white)}>Remove {dupes.extra} duplicate{dupes.extra === 1 ? '' : 's'}</button>
              : <button onClick={checkDupes} disabled={busy || !photos.length} style={ghost()}>Find duplicates</button>}
          </div>
          {/* Selection bar — only exists while something is selected */}
          {view === 'grid' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, minHeight: 30 }}>
              {picked.size ? (
                <>
                  <span style={{ fontSize: 12, fontWeight: 700, color: palette.brown }}>{picked.size} selected</span>
                  <button onClick={() => bulk('hide')} disabled={busy} style={btn(palette.brown, palette.white)}>Hide</button>
                  <button onClick={() => bulk('show')} disabled={busy} style={ghost()}>Show</button>
                  <button onClick={() => bulk('delete')} disabled={busy} style={ghost({ color: palette.red })}>Delete</button>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => setPicked(new Set(photos.map((p) => p.id)))} style={ghost()}>All</button>
                  <button onClick={() => setPicked(new Set())} style={ghost()}>Clear</button>
                </>
              ) : (
                <span style={{ fontSize: 12, color: palette.textMuted }}>Tap frames to select, then hide, show or delete them together.</span>
              )}
            </div>
          )}

          {/* Contact sheet — the fast way to weed a set */}
          {view === 'grid' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, border: `1px solid ${palette.border}`, background: palette.white, padding: 10 }}>
              {!photos.length && <div style={{ gridColumn: '1 / -1', color: palette.textMuted, fontSize: 13, textAlign: 'center', padding: 24 }}>No photos yet.</div>}
              {photos.map((p, idx) => {
                const on = picked.has(p.id);
                return (
                  <div key={p.id} onClick={() => togglePick(p.id)} title={p.subject_name || p.slug}
                    style={{ position: 'relative', cursor: 'pointer', outline: on ? `3px solid ${palette.orange}` : `1px solid ${palette.border}`, outlineOffset: -1, background: palette.cream, aspectRatio: '4 / 5', opacity: p.published ? 1 : 0.4 }}>
                    {p.has_hero && (
                      <img src={thumbUrl(gallery.slug, p.slug, 400)}
                        alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    )}
                    <div style={{ position: 'absolute', top: 6, left: 6, width: 18, height: 18, borderRadius: 3, background: on ? palette.orange : 'rgba(255,255,255,0.9)', border: `1px solid ${on ? palette.orange : palette.border}`, color: palette.white, fontSize: 13, lineHeight: '18px', textAlign: 'center', fontWeight: 700 }}>{on ? '✓' : ''}</div>
                    {!p.published && (
                      <div style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', background: palette.brown, color: palette.white, padding: '2px 6px' }}>Hidden</div>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, right: 0, fontSize: 10, background: 'rgba(255,255,255,0.85)', color: palette.text, padding: '2px 5px', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.subject_name || `#${idx + 1}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Portraits table */}
          {view === 'table' && <div style={{ overflowX: 'auto', border: `1px solid ${palette.border}`, background: palette.white }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: palette.cream }}>
                  <th style={th}></th>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Link</th>
                  <th style={th}>Downloads</th>
                  <th style={th}>List</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {!photos.length && (
                  <tr><td colSpan={7} style={{ ...td, color: palette.textMuted, textAlign: 'center', padding: 24 }}>No photos yet.</td></tr>
                )}
                {photos.map((p) => {
                  const d = rowDrafts[p.id];
                  const edit = (field, v) => setRowDrafts((prev) => ({ ...prev, [p.id]: { name: p.subject_name || '', email: p.subject_email || '', caption: p.caption || '', ...(prev[p.id] || {}), [field]: v } }));
                  return (
                    <tr key={p.id} style={{ opacity: p.published ? 1 : 0.55 }}>
                      <td style={{ ...td, width: 56 }}>
                        {p.has_hero && (
                          <img src={thumbUrl(gallery.slug, p.slug, 200)}
                            alt="" style={{ width: 48, height: 60, objectFit: 'cover', display: 'block', background: palette.cream }}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                        )}
                      </td>
                      <td style={{ ...td, minWidth: 150 }}>
                        <input style={input} value={d ? d.name : (p.subject_name || '')} onChange={(e) => edit('name', e.target.value)} placeholder="Name" />
                      </td>
                      <td style={{ ...td, minWidth: 190 }}>
                        <input style={input} value={d ? d.email : (p.subject_email || '')} onChange={(e) => edit('email', e.target.value)} placeholder="Email" />
                      </td>
                      <td style={td}>
                        <a href={`/events/${encodeURIComponent(gallery.slug)}/${encodeURIComponent(p.slug)}`} target="_blank" rel="noreferrer" style={{ color: palette.orange, textDecoration: 'none', fontSize: 11 }}>
                          /{p.slug} ↗
                        </a>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} title={(p.downloaded_by || []).join(', ')}>
                        {p.free_downloads || 0}{p.downloaded_by?.length ? ` · ${p.downloaded_by.length} ${p.downloaded_by.length === 1 ? 'person' : 'people'}` : ''}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.subscribed ? <span style={{ color: palette.sage, fontWeight: 700 }}>✓</span> : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {d && <button onClick={() => savePhoto(p)} disabled={busy} style={btn(palette.brown, palette.white)}>Save</button>}
                          <button onClick={() => togglePhoto(p)} disabled={busy} style={ghost()}>{p.published ? 'Hide' : 'Show'}</button>
                          <button onClick={() => deletePhoto(p)} disabled={busy} style={ghost({ color: palette.red })}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// People — everyone we've met, across every event and Paws & Portraits
// ---------------------------------------------------------------------------

export function PeoplePanel({ palette, fonts }) {
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | subscribed | pet-owner | <event slug>
  const [note, setNote] = useState('');

  useEffect(() => {
    api('/api/admin/people').then((d) => setPeople(d.people || [])).catch((e) => { setNote(`✗ ${e.message}`); setPeople([]); });
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/admin/people.csv`, { headers: authHeader() });
      if (!r.ok) throw new Error('Could not build the list');
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'hddn-people.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { setNote(`✗ ${err.message}`); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: palette.textMuted, whiteSpace: 'nowrap' };
  const td = { padding: '10px', fontFamily: fonts, fontSize: 12, color: palette.text, verticalAlign: 'top', borderBottom: `1px solid ${palette.border}` };
  const btn = (bg, fg, extra = {}) => ({ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 11px', background: bg, color: fg, border: 'none', cursor: 'pointer', ...extra });
  const ghost = (extra = {}) => btn('none', palette.brown, { border: `1px solid ${palette.border}`, ...extra });
  const input = { fontFamily: fonts, fontSize: 12, padding: '7px 9px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text };

  if (people === null) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading people…</div>;

  const events = [...new Map(people.flatMap((p) => p.met.map((m) => [m.event, m.metAt]))).entries()];
  const term = q.trim().toLowerCase();
  const shown = people.filter((p) => {
    if (filter === 'subscribed' && !p.subscribed) return false;
    if (filter === 'pet-owner' && !(p.tags || []).includes('pet owner')) return false;
    if (!['all', 'subscribed', 'pet-owner'].includes(filter) && !p.met.some((m) => m.event === filter)) return false;
    if (term && !(`${p.name || ''} ${p.email}`.toLowerCase().includes(term))) return false;
    return true;
  });
  const when = (ts) => new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div style={{ fontFamily: fonts }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input style={{ ...input, flex: '1 1 200px' }} placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <select style={input} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Everyone ({people.length})</option>
          <option value="subscribed">On the newsletter ({people.filter((p) => p.subscribed).length})</option>
          <option value="pet-owner">Pet owners ({people.filter((p) => (p.tags || []).includes('pet owner')).length})</option>
          {events.map(([slug, title]) => <option key={slug} value={slug}>{title}</option>)}
        </select>
        <button onClick={downloadCsv} style={ghost()}>Export CSV</button>
      </div>
      {note && <div style={{ fontSize: 12, color: palette.red, marginBottom: 10 }}>{note}</div>}
      <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 8 }}>{shown.length} {shown.length === 1 ? 'person' : 'people'}. Built from every event's list and the Paws & Portraits sessions — add people through an event, not here.</div>
      <div style={{ overflowX: 'auto', border: `1px solid ${palette.border}`, background: palette.white }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: palette.cream }}><th style={th}>Name</th><th style={th}>Email</th><th style={th}>Where we met</th><th style={th}>Tags</th><th style={th}>Newsletter</th></tr></thead>
          <tbody>
            {!shown.length && <tr><td colSpan={5} style={{ ...td, color: palette.textMuted, textAlign: 'center', padding: 24 }}>Nobody matches.</td></tr>}
            {shown.map((p) => (
              <tr key={p.email}>
                <td style={td}>{p.name || <span style={{ color: palette.textMuted }}>—</span>}</td>
                <td style={td}>{p.email}</td>
                <td style={td}>
                  {p.met.map((m, i) => (
                    <div key={i} style={{ marginBottom: 2 }}>
                      <b>{m.metAt}</b> <span style={{ color: palette.textMuted }}>· {m.how}{m.note ? ` · ${m.note}` : ''} · {when(m.on)}</span>
                    </div>
                  ))}
                </td>
                <td style={td}>{(p.tags || []).map((t) => <span key={t} style={{ display: 'inline-block', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', background: palette.cream, border: `1px solid ${palette.border}`, padding: '2px 6px', marginRight: 4 }}>{t}</span>)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.subscribed ? <span style={{ color: palette.sage, fontWeight: 700 }}>✓</span> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
